import React, { useState, useCallback } from 'react';
import { Grid } from '@material-ui/core';

import { listHinovaProducts } from '~/services/api';
import { handleApiErrorResponse } from '~/services/handleErrors';
import { HinovaProduct } from '~/types';
import { Table } from '~/components/Table';
import InputComponent from '~/components/InputComponent';
import Button from '~/components/Button';
import Snackbar from '~/components/Snackbar/index';
import { SeverityType } from '~/components/Snackbar/index';

export default function ProductSearch() {
  const [codigoRegional, setCodigoRegional] = useState<string>('');
  const [codigoCooperativa, setCodigoCooperativa] = useState<string>('');
  const [codigoTipoVeiculo, setCodigoTipoVeiculo] = useState<string>('');
  const [valorFipe, setValorFipe] = useState<string>('');
  const [cilindrada, setCilindrada] = useState<string>('');
  const [products, setProducts] = useState<HinovaProduct[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [messageSnack, setMessageSnack] = useState('');
  const [severity, setSeverity] = useState<SeverityType>('info');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const searchProducts = useCallback(async () => {
    if (!codigoRegional || !codigoCooperativa || !codigoTipoVeiculo || !valorFipe || !cilindrada) {
      setSeverity('warning');
      setMessageSnack('Por favor, preencha todos os campos obrigatórios.');
      setOpenSnackbar(true);
      return;
    }

    try {
      setLoading(true);
      const response = await listHinovaProducts(
        codigoRegional,
        codigoCooperativa,
        codigoTipoVeiculo,
        valorFipe,
        cilindrada,
      );

      if (response.status === 200 && response.data) {
        const data = response.data;
        const productsData = Array.isArray(data) ? data : [data];
        setProducts(productsData);
      } else {
        setProducts([]);
      }
    } catch (err) {
      const error = handleApiErrorResponse(err);
      setSeverity('warning');
      setMessageSnack(
        (error?.applicationErrorMessage || error?.apiError) + ': não foi possível buscar produtos. Tente novamente.',
      );
      setOpenSnackbar(true);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [codigoRegional, codigoCooperativa, codigoTipoVeiculo, valorFipe, cilindrada]);

  return (
    <>
      <Grid container spacing={2} style={{ marginBottom: 20 }} alignItems='flex-end'>
        <Grid item xs={12} sm={6} md={4}>
          <InputComponent
            label='Código Regional'
            name='codigoRegional'
            type='text'
            value={codigoRegional}
            onChange={e => setCodigoRegional(e.target.value)}
            error={null}
            placeholder='Código da Regional'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <InputComponent
            label='Código Cooperativa'
            name='codigoCooperativa'
            type='text'
            value={codigoCooperativa}
            onChange={e => setCodigoCooperativa(e.target.value)}
            error={null}
            placeholder='Código da Cooperativa'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <InputComponent
            label='Código Tipo Veículo'
            name='codigoTipoVeiculo'
            type='text'
            value={codigoTipoVeiculo}
            onChange={e => setCodigoTipoVeiculo(e.target.value)}
            error={null}
            placeholder='Código do Tipo de Veículo'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <InputComponent
            label='Valor FIPE'
            name='valorFipe'
            type='number'
            value={valorFipe}
            onChange={e => setValorFipe(e.target.value)}
            error={null}
            placeholder='Valor FIPE'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <InputComponent
            label='Cilindrada'
            name='cilindrada'
            type='text'
            value={cilindrada}
            onChange={e => setCilindrada(e.target.value)}
            error={null}
            placeholder='Cilindrada'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Button
            label='Buscar'
            loading={loading}
            onClick={searchProducts}
            filled
            style={{ width: '100%', marginTop: 0 }}
          />
        </Grid>
      </Grid>

      <Table
        isLoading={loading}
        data={products}
        columns={{
          codigo: {
            title: 'Código',
          },
          descricao: {
            title: 'Descrição',
            render: rowData => rowData.descricao || '-',
          },
          valor: {
            title: 'Valor',
            render: rowData => (rowData.valor ? `R$ ${rowData.valor.toFixed(2)}` : '-'),
          },
        }}
        localization={{
          body: {
            emptyDataSourceMessage: loading
              ? 'Carregando...'
              : 'Nenhum produto encontrado. Preencha todos os campos e tente novamente.',
          },
          pagination: {
            labelRowsSelect: 'produtos',
          },
        }}
        options={{
          showTitle: false,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          paging: true,
          filtering: false,
          search: false,
        }}
      />

      <Snackbar message={messageSnack} open={openSnackbar} severity={severity} onClose={() => setOpenSnackbar(false)} />
    </>
  );
}
